import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  X,
  ArrowDownAZ,
  CalendarClock,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { copy } from '@/lib/copy'
import { EmptyState } from '@/components/EmptyState'
import { ErrorScreen } from '@/components/ErrorScreen'
import { SkeletonList } from '@/components/SkeletonList'
import {
  VocabularyWordList,
} from '@/components/vocabulary/VocabularyWordList'
import { AlphabetJumpBar } from '@/components/vocabulary/AlphabetJumpBar'
import { AddWordForm, type AddedVocabularyItem } from '@/components/vocabulary/AddWordForm'
import { AddToCollectionSheet } from '@/components/vocabulary/AddToCollectionSheet'
import { CollectionsPanel } from '@/components/vocabulary/CollectionsPanel'
import { ExpandableFab } from '@/components/mobile/ExpandableFab'
import { FilterTriggerButton } from '@/components/mobile/FilterTriggerButton'
import { MobileBottomSheet, SheetOptionButton } from '@/components/mobile/MobileBottomSheet'
import { vocabularyApi } from '@/api/vocabulary'
import type { WordStatus, CefrLevel } from '@/types/common'
import type { UserWordSummary } from '@/types/user'
import { useDebounce } from '@/hooks/useDebounce'
import { useMobileViewport } from '@/hooks/useMobileViewport'
import { useSettingsStore } from '@/store/settingsStore'
import { loadVocabularySort, saveVocabularySort, type VocabularySortMode } from '@/lib/vocabularySortStorage'
import {
  scrollVocabularyItemIntoView,
  scrollVocabularyLetterIntoView,
} from '@/lib/scrollVocabularyItem'
import { cn } from '@/components/ui/cn'

const CEFR_OPTIONS: { value: CefrLevel | ''; label: string }[] = [
  { value: '', label: 'All levels' },
  { value: 'A1', label: 'A1' },
  { value: 'A2', label: 'A2' },
  { value: 'B1', label: 'B1' },
  { value: 'B2', label: 'B2' },
  { value: 'C1', label: 'C1' },
  { value: 'C2', label: 'C2' },
]

type UnitFilter = 'all' | 'words' | 'phrases' | 'idioms' | 'phrasal_verbs'

const UNIT_FILTER_OPTIONS: { value: UnitFilter; label: string }[] = [
  { value: 'all', label: copy.vocabulary.unitFilterAll },
  { value: 'words', label: copy.vocabulary.unitFilterWords },
  { value: 'phrases', label: copy.vocabulary.unitFilterPhrases },
  { value: 'idioms', label: copy.vocabulary.unitFilterIdioms },
  { value: 'phrasal_verbs', label: copy.vocabulary.unitFilterPhrasal },
]

const PAGE_SIZE = 100
const PHRASE_FETCH_SIZE = 500

function phraseMatchesUnitFilter(unitType: string | undefined, filter: UnitFilter): boolean {
  const t = (unitType ?? 'PHRASE').toUpperCase()
  if (filter === 'phrases') {
    return t === 'PHRASE'
  }
  if (filter === 'idioms') {
    return t === 'IDIOM'
  }
  if (filter === 'phrasal_verbs') {
    return t === 'PHRASAL_VERB'
  }
  // all
  return true
}

function mapPhraseToWord(p: {
  id: string
  phraseId: string
  surface: string
  translation: string
  status: WordStatus
  nextReviewAt?: string
  reviewCount: number
  source: UserWordSummary['source']
  dateAdded?: string
  unitType: string
}): UserWordSummary {
  return {
    id: p.id,
    wordId: p.phraseId,
    word: p.surface,
    translation: p.translation,
    status: p.status,
    nextReviewAt: p.nextReviewAt,
    reviewCount: p.reviewCount,
    source: p.source,
    knownSenseCount: 1,
    knownTranslations: [p.translation],
    dateAdded: p.dateAdded,
    unitKind: 'PHRASE',
    phraseUnitType: p.unitType,
  }
}

export function VocabularyPage() {
  const queryClient = useQueryClient()
  const nativeLanguage = useSettingsStore((s) => s.settings?.nativeLanguage ?? 'en')
  const isMobile = useMobileViewport(1023)
  const listScrollRef = useRef<HTMLDivElement>(null)
  const pendingScrollRef = useRef<AddedVocabularyItem | null>(null)
  const highlightClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showMobileCollections, setShowMobileCollections] = useState(false)
  const [cefrSheetOpen, setCefrSheetOpen] = useState(false)
  const [unitSheetOpen, setUnitSheetOpen] = useState(false)
  const [collectionWord, setCollectionWord] = useState<UserWordSummary | null>(null)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<WordStatus | ''>('')
  const [dueOnly, setDueOnly] = useState(false)
  const [cefrLevel, setCefrLevel] = useState<CefrLevel | ''>('')
  const [unitFilter, setUnitFilter] = useState<UnitFilter>('all')
  const [collectionId, setCollectionId] = useState<string | undefined>()
  const [page, setPage] = useState(0)
  const [loadedWords, setLoadedWords] = useState<UserWordSummary[]>([])
  const [sortMode, setSortMode] = useState<VocabularySortMode>(() => loadVocabularySort())
  const [jumpSections, setJumpSections] = useState<string[]>([])
  const [highlightWordId, setHighlightWordId] = useState<string | null>(null)
  /** Bumps when a word is added so the scroll effect re-runs without clearing mid-timer. */
  const [scrollRequestId, setScrollRequestId] = useState(0)

  useEffect(() => {
    setCollectionId(undefined)
  }, [nativeLanguage])

  const debouncedSearch = useDebounce(search, 300)

  const wantsWords = unitFilter === 'all' || unitFilter === 'words'
  const wantsPhrases = unitFilter !== 'words'
  const phraseOnly = unitFilter === 'phrases' || unitFilter === 'idioms' || unitFilter === 'phrasal_verbs'

  const filters = {
    search: debouncedSearch || undefined,
    status: dueOnly ? undefined : (status || undefined),
    cefrLevel: cefrLevel || undefined,
    collectionId,
    dueOnly: dueOnly || undefined,
    sortBy: sortMode === 'dateAdded' ? 'DATE_ADDED' as const : 'ALPHABETICAL' as const,
    page,
    size: PAGE_SIZE,
  }

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['vocabularyStats'],
    queryFn: () => vocabularyApi.getStats().then((r) => r.data),
  })

  const {
    data,
    isLoading: wordsLoading,
    isFetching: wordsFetching,
    isError: wordsError,
    refetch: refetchWords,
  } = useQuery({
    queryKey: ['vocabulary', nativeLanguage, filters],
    queryFn: () => vocabularyApi.getWords(filters).then((r) => r.data),
    enabled: wantsWords && !phraseOnly,
    placeholderData: (prev) => prev,
    refetchOnMount: 'always',
  })

  const phrasesEnabled =
    wantsPhrases
    && !collectionId
    && !filters.cefrLevel
    && !filters.dueOnly

  const {
    data: phrasesData,
    isLoading: phrasesLoading,
    isFetching: phrasesFetching,
    isError: phrasesError,
    refetch: refetchPhrases,
  } = useQuery({
    queryKey: ['vocabularyPhrases', nativeLanguage, {
      search: filters.search,
      status: filters.status,
      size: PHRASE_FETCH_SIZE,
    }],
    queryFn: () => vocabularyApi.getPhrases({
      search: filters.search,
      status: filters.status,
      page: 0,
      size: PHRASE_FETCH_SIZE,
    }).then((r) => r.data),
    enabled: phrasesEnabled,
  })

  // Accumulate word pages for All (Load more); replace for Words pager / filter reset.
  useEffect(() => {
    if (!wantsWords || phraseOnly) {
      setLoadedWords([])
      return
    }
    const pageContent = data?.content ?? []
    if (unitFilter === 'words' || page === 0) {
      setLoadedWords(pageContent)
      return
    }
    setLoadedWords((prev) => {
      const seen = new Set(prev.map((w) => w.id))
      const appended = pageContent.filter((w) => !seen.has(w.id))
      return appended.length === 0 ? prev : [...prev, ...appended]
    })
  }, [data, page, phraseOnly, unitFilter, wantsWords])

  const phraseAsWords: UserWordSummary[] = (phrasesData?.content ?? [])
    .filter((p) => phraseMatchesUnitFilter(p.unitType, unitFilter === 'words' ? 'all' : unitFilter))
    .map(mapPhraseToWord)

  const words: UserWordSummary[] = (() => {
    if (phraseOnly) {
      return [...phraseAsWords].sort((a, b) => {
        if (sortMode === 'dateAdded') {
          return (b.dateAdded ?? '').localeCompare(a.dateAdded ?? '')
        }
        return a.word.localeCompare(b.word)
      })
    }
    if (unitFilter === 'words' || collectionId || filters.cefrLevel || filters.dueOnly) {
      return loadedWords
    }
    // all — merge loaded words + phrases
    return [...loadedWords, ...phraseAsWords].sort((a, b) => {
      if (sortMode === 'dateAdded') {
        return (b.dateAdded ?? '').localeCompare(a.dateAdded ?? '')
      }
      return a.word.localeCompare(b.word)
    })
  })()

  const isLoading = (wantsWords && !phraseOnly && wordsLoading && page === 0)
    || (phrasesEnabled && phrasesLoading && phraseOnly)
  const isFetching = wordsFetching || phrasesFetching
  const isError = (wantsWords && !phraseOnly && wordsError) || (phrasesEnabled && phrasesError)
  const refetch = () => {
    if (wantsWords && !phraseOnly) void refetchWords()
    if (phrasesEnabled) void refetchPhrases()
  }

  const totalPages = data?.totalPages ?? 0
  const canLoadMoreWords = wantsWords && !phraseOnly && page + 1 < totalPages
  const showPager = unitFilter === 'words' && totalPages > 1

  // Keep loading pages until the full vocabulary is in memory (PAGE_SIZE chunks).
  // Without this, alphabetical letters past the first page (e.g. W) never appear in the jump bar,
  // and newly (re)added late-alphabet cards look "missing" until later pages load.
  useEffect(() => {
    if (unitFilter !== 'all' || !canLoadMoreWords || wordsFetching || wordsLoading) return
    setPage((p) => p + 1)
  }, [unitFilter, canLoadMoreWords, wordsFetching, wordsLoading, page, totalPages])

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      const row = words.find((w) => w.id === id)
      if (row?.unitKind === 'PHRASE') {
        return vocabularyApi.deletePhrase(id)
      }
      return vocabularyApi.deleteWord(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      queryClient.invalidateQueries({ queryKey: ['knownPhrases'] })
    },
  })

  const removeFromCollectionMutation = useMutation({
    mutationFn: (userWordId: string) => {
      if (!collectionId) {
        return Promise.reject(new Error('No collection selected'))
      }
      return vocabularyApi.removeWordFromCollection(collectionId, userWordId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      queryClient.invalidateQueries({ queryKey: ['collections'] })
    },
  })

  const handleWordDelete = (id: string) => {
    if (collectionId) {
      removeFromCollectionMutation.mutate(id)
      return
    }
    deleteMutation.mutate(id)
  }

  const deleteAriaLabel = collectionId ? copy.vocabulary.removeFromCollection : copy.vocabulary.deleteWord

  const handleFilterChange = useCallback(() => {
    setPage(0)
    setLoadedWords([])
  }, [])
  const handleSectionsChange = useCallback((sections: string[]) => {
    setJumpSections(sections)
  }, [])

  const handleWordAdded = useCallback((item: AddedVocabularyItem) => {
    pendingScrollRef.current = item
    // Clear filters that would hide the (re)added card, then scroll to it.
    setStatus('')
    setDueOnly(false)
    setPage(0)
    setLoadedWords([])
    setScrollRequestId((n) => n + 1)
  }, [])

  useEffect(() => {
    return () => {
      if (highlightClearTimerRef.current) clearTimeout(highlightClearTimerRef.current)
    }
  }, [])

  // After add: poll until the new card is in the DOM, then scroll.
  // Do not depend on `words` (new array every render) — that cancelled the timer.
  // Cards past the first PAGE_SIZE (e.g. alphabetical "wasteland" at rank 114)
  // appear after the All-view auto-page effect finishes loading.
  useEffect(() => {
    if (!scrollRequestId) return

    let cancelled = false
    const startedAt = Date.now()
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = () => {
      if (cancelled) return
      const pending = pendingScrollRef.current
      if (!pending) return

      if (Date.now() - startedAt > 12_000) {
        pendingScrollRef.current = null
        if (sortMode === 'alphabetical') {
          scrollVocabularyLetterIntoView(pending.surface)
        } else {
          listScrollRef.current?.scrollTo({ top: 0 })
        }
        return
      }

      const byId = document.querySelector<HTMLElement>(
        `[data-user-word-id="${CSS.escape(pending.id)}"]`,
      )
      const bySurface = byId ?? document.querySelector<HTMLElement>(
        `[data-testid="word-card-${CSS.escape(pending.surface)}"]`,
      )
      if (!bySurface) {
        timer = setTimeout(tick, 80)
        return
      }

      const targetId = bySurface.dataset.userWordId ?? pending.id
      pendingScrollRef.current = null
      scrollVocabularyItemIntoView(targetId, listScrollRef.current)
      setHighlightWordId(targetId)
      if (highlightClearTimerRef.current) clearTimeout(highlightClearTimerRef.current)
      highlightClearTimerRef.current = setTimeout(() => setHighlightWordId(null), 1600)
    }

    // Let the add modal unmount before measuring layout.
    timer = setTimeout(tick, 100)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [scrollRequestId, sortMode])

  const handleSortModeChange = (mode: VocabularySortMode) => {
    setSortMode(mode)
    saveVocabularySort(mode)
    handleFilterChange()
  }

  useEffect(() => {
    if (words.length === 0) {
      setJumpSections([])
    }
  }, [words.length])

  const showAlphabetJump = sortMode === 'alphabetical' && words.length > 0 && jumpSections.length > 1
  const activeCefrLabel = CEFR_OPTIONS.find((o) => o.value === cefrLevel)?.label ?? 'All levels'
  const activeUnitLabel = UNIT_FILTER_OPTIONS.find((o) => o.value === unitFilter)?.label ?? copy.vocabulary.unitFilterAll

  return (
    <div>
      {/* ── Compact header ─────────────────────────────────────── */}
      <div className="mb-4 hidden items-center justify-between gap-4 lg:flex">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{copy.pages.words}</h1>
          {stats ? (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {copy.vocabulary.itemCount(stats.total)}
              {stats.byStatus.LEARNING > 0 && (
                <> · <span className="text-blue-400">{stats.byStatus.LEARNING} learning</span></>
              )}
              {stats.byStatus.NEW > 0 && (
                <> · <span className="text-muted-foreground">{stats.byStatus.NEW} new</span></>
              )}
              {stats.dueToday > 0 && (
                <> · <span className="text-orange-400">{stats.dueToday} due today</span></>
              )}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Status quick-filters */}
          {stats && !statsLoading && (
            <div className="hidden items-center gap-1 sm:flex">
              {(['NEW', 'LEARNING', 'REVIEW', 'MASTERED'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setDueOnly(false)
                    setStatus(status === s ? '' : s)
                    handleFilterChange()
                  }}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    status === s && !dueOnly
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {s[0] + s.slice(1).toLowerCase()} {stats.byStatus[s] ?? 0}
                </button>
              ))}
              {stats.dueToday > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setDueOnly((prev) => !prev)
                    setStatus('')
                    handleFilterChange()
                  }}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    dueOnly
                      ? 'border-orange-400 bg-orange-500/10 text-orange-400'
                      : 'border-orange-500/30 text-orange-500/70 hover:border-orange-400/60 hover:text-orange-400',
                  )}
                >
                  Due {stats.dueToday}
                </button>
              )}
            </div>
          )}
          <Button className="w-auto gap-2" onClick={() => setShowAddForm(true)}>
            <Plus className="h-4 w-4" />
            {copy.vocabulary.addWordTitle}
          </Button>
        </div>
      </div>

      <ExpandableFab
        label={copy.vocabulary.addWordTitle}
        onAction={() => setShowAddForm(true)}
        testId="vocabulary-add-fab"
      />

      <div className="flex min-h-0 flex-1 gap-4 lg:gap-6">
        <div className="hidden w-56 shrink-0 lg:block">
          <CollectionsPanel
            selectedId={collectionId}
            onSelect={(id) => { setCollectionId(id); handleFilterChange() }}
          />
        </div>

        <div className="flex min-w-0 flex-1 gap-3">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="mb-4 shrink-0 space-y-3">
              <div className="flex flex-wrap gap-2">
                <div className="relative min-w-48 flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder={copy.vocabulary.searchPlaceholder}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); handleFilterChange() }}
                  />
                </div>

                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm lg:hidden"
                  onClick={() => setShowMobileCollections(true)}
                >
                  <FolderOpen className="h-4 w-4" />
                  Collections
                </button>

                <div
                  className="flex rounded-md border bg-background p-0.5"
                  data-testid="vocabulary-sort-toggle"
                >
                  <button
                    type="button"
                    aria-label={copy.vocabulary.sortAlphabeticalAria}
                    title={copy.vocabulary.sortAlphabeticalAria}
                    onClick={() => handleSortModeChange('alphabetical')}
                    className={cn(
                      'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                      sortMode === 'alphabetical'
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    data-testid="vocabulary-sort-alphabetical"
                  >
                    <ArrowDownAZ className="h-4 w-4" />
                    <span className="hidden sm:inline">{copy.vocabulary.sortAlphabetical}</span>
                  </button>
                  <button
                    type="button"
                    aria-label={copy.vocabulary.sortDateAddedAria}
                    title={copy.vocabulary.sortDateAddedAria}
                    onClick={() => handleSortModeChange('dateAdded')}
                    className={cn(
                      'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                      sortMode === 'dateAdded'
                        ? 'bg-accent text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    data-testid="vocabulary-sort-date-added"
                  >
                    <CalendarClock className="h-4 w-4" />
                    <span className="hidden sm:inline">{copy.vocabulary.sortDateAdded}</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 lg:hidden" data-testid="vocabulary-mobile-filters">
                <FilterTriggerButton
                  label={activeUnitLabel}
                  onClick={() => setUnitSheetOpen(true)}
                  testId="vocabulary-filter-unit"
                />
                <FilterTriggerButton
                  label={activeCefrLabel}
                  onClick={() => setCefrSheetOpen(true)}
                  testId="vocabulary-filter-cefr"
                />
              </div>

              <div className="hidden flex-wrap gap-1.5 lg:flex" data-testid="vocabulary-unit-filters">
                {UNIT_FILTER_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      setUnitFilter(o.value)
                      handleFilterChange()
                    }}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      unitFilter === o.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              <div className="hidden flex-wrap gap-1.5 lg:flex">
                {CEFR_OPTIONS.map((o) => (
                  <button
                    key={o.value || 'all'}
                    type="button"
                    onClick={() => {
                      setCefrLevel(o.value)
                      handleFilterChange()
                    }}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      cefrLevel === o.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                    )}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <SkeletonList rows={8} />
            ) : isError ? (
              <ErrorScreen
                variant="network"
                title="Couldn't load vocabulary"
                description="Please check your connection and try again."
                onRetry={() => refetch()}
              />
            ) : words.length === 0 ? (
              <EmptyState
                title="No words yet"
                description={
                  dueOnly
                    ? 'No words are due for review right now.'
                    : search || status || cefrLevel
                      ? 'Try adjusting your filters.'
                      : 'Start building your vocabulary by adding your first word.'
                }
                action={
                  !search && !status && !cefrLevel ? (
                    <Button
                      className="w-auto gap-2 lg:inline-flex"
                      onClick={() => setShowAddForm(true)}
                      data-testid="vocabulary-empty-add"
                    >
                      <Plus className="h-4 w-4" />
                      Add your first word
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <div
                  ref={listScrollRef}
                  className={cn(
                    'min-h-0 flex-1 overflow-y-auto rounded-xl border border-border/40 bg-card/30 p-2 transition-opacity',
                    isFetching && 'opacity-60',
                  )}
                  style={{ maxHeight: 'calc(100vh - 13rem)' }}
                >
                  <VocabularyWordList
                    words={words}
                    statusFilter={dueOnly ? '' : status}
                    sortMode={sortMode}
                    onDelete={handleWordDelete}
                    deleteAriaLabel={deleteAriaLabel}
                    searchQuery={debouncedSearch}
                    onSectionsChange={handleSectionsChange}
                    enableDrag={!isMobile}
                    onAddToCollection={isMobile ? setCollectionWord : undefined}
                    highlightWordId={highlightWordId}
                  />
                </div>

                {showPager && (
                  <div className="mt-4 flex shrink-0 items-center justify-center gap-3">
                    <button
                      className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 0}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="text-sm text-muted-foreground">
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages - 1}
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {showAlphabetJump && (
            <AlphabetJumpBar letters={jumpSections} className="shrink-0 self-start" />
          )}
        </div>
      </div>

      {showMobileCollections && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileCollections(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t bg-background p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold">Collections</h3>
              <button type="button" onClick={() => setShowMobileCollections(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <CollectionsPanel
              selectedId={collectionId}
              onSelect={(id) => {
                setCollectionId(id)
                handleFilterChange()
                setShowMobileCollections(false)
              }}
            />
          </div>
        </div>
      )}

      {showAddForm && (
        <AddWordForm onClose={() => setShowAddForm(false)} onAdded={handleWordAdded} />
      )}

      <AddToCollectionSheet
        word={collectionWord}
        open={collectionWord != null}
        onClose={() => setCollectionWord(null)}
      />

      <MobileBottomSheet
        open={unitSheetOpen}
        title="Type"
        onClose={() => setUnitSheetOpen(false)}
        testId="vocabulary-unit-sheet"
      >
        {UNIT_FILTER_OPTIONS.map((o) => (
          <SheetOptionButton
            key={o.value}
            active={unitFilter === o.value}
            label={o.label}
            onClick={() => {
              setUnitFilter(o.value)
              handleFilterChange()
              setUnitSheetOpen(false)
            }}
          />
        ))}
      </MobileBottomSheet>

      <MobileBottomSheet
        open={cefrSheetOpen}
        title="Level"
        onClose={() => setCefrSheetOpen(false)}
        testId="vocabulary-cefr-sheet"
      >
        {CEFR_OPTIONS.map((o) => (
          <SheetOptionButton
            key={o.value || 'all'}
            active={cefrLevel === o.value}
            label={o.label}
            onClick={() => {
              setCefrLevel(o.value)
              handleFilterChange()
              setCefrSheetOpen(false)
            }}
          />
        ))}
      </MobileBottomSheet>
    </div>
  )
}
